import { Tabs } from "antd";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import TabHeader from "./component/TabHeader";
import TabContent from "./component/TabContent";
import { updateQuery } from "@utils/helpers";
import {
  useFetchSectionMonthsQuery,
  useFetchSectionProductsQuery,
} from "@store/master";

export default function PageLayout({
  title,
  tabs = [],
  onMonthChange,
  month,
  onYearChange,
  year,
  onProductChange,
  product,
  productOptions,
  monthOptions,
  yearOptions,
  drawerInputsToShow,
  filtersToShow,
  uploadUrls = {},
  saveAndGenerateUrls,
  redirectUrl,
  useDefaultSheetNames,
  fetchProductsWiseData,
  fetchRawAndFinalFileUrls,
  sectionProductsTypes,
  sectionMonthsTypes,
  sectionTypeValues,
  previewSelectInputFields,
  previewLockInputFields,
  subTypeId,
}) {
  const [activeTab, setActiveTab] = useState(null);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = params.get("tab");

  const { data: products } = useFetchSectionProductsQuery(
    {
      type: sectionProductsTypes[activeTab],
      month,
      year,
    },
    {
      skip: !fetchProductsWiseData,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: months } = useFetchSectionMonthsQuery(
    {
      type: sectionMonthsTypes[activeTab],
      year,
    },
    {
      skip: fetchProductsWiseData,
      refetchOnMountOrArgChange: true,
    }
  );

  const updateQueryWithTabChange = (tabKey) => {
    updateQuery(location, { tab: tabKey });
    setActiveTab(tabKey);
  };

  //updating params on refresh
  useEffect(() => {
    if (!tab) {
      updateQueryWithTabChange(tabs[0].value);
    } else {
      updateQueryWithTabChange(tab);
    }
  }, [tab]);

  const items = tabs.map((tab) => {
    return { key: tab.value, label: tab.label };
  });

  return (
    <div className="h-full p-2 overflow-x-hidden overflow-y-auto rounded-lg md:p-5 animate-fadeIn bg-secondary-main custom-scrollbar">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col items-start justify-between border-b md:items-center md:flex-row border-b-border">
          {/* label */}
          <label className="text-lg font-semibold leading-6 text-text-header">
            {title}
          </label>
          {/* tabs */}
          {/* mobile */}
          <div className="flex w-full md:hidden">
            <Tabs
              items={items}
              tabBarStyle={{ margin: 0, padding: 0 }}
              className="w-full mt-2 overflow-x-auto overflow-y-hidden custom-tabs [&_.ant-tabs-nav-operations]:!hidden"
              tabBarGutter={43}
              activeKey={activeTab}
              onChange={(key) => updateQueryWithTabChange(key)}
            />
          </div>
          {/* desktop */}
          <div className="hidden md:flex">
            <Tabs
              items={items}
              tabBarStyle={{ margin: 0, padding: 0 }}
              className="custom-tabs"
              tabBarGutter={43}
              activeKey={activeTab}
              onChange={(key) => updateQueryWithTabChange(key)}
            />
          </div>
        </div>
        {/* tabs content */}
        {/* header */}
        <TabHeader
          lastUpdateDate={
            fetchProductsWiseData ? products?.lastUpdated : months?.lastUpdated
          }
          onMonthChange={onMonthChange}
          month={month}
          onYearChange={onYearChange}
          year={year}
          monthOptions={monthOptions}
          yearOptions={yearOptions}
          onProductChange={onProductChange}
          product={product}
          productOptions={productOptions}
          drawerInputsToShow={drawerInputsToShow}
          filtersToShow={filtersToShow}
          url={uploadUrls[activeTab]}
          activeTab={activeTab}
          saveAndGenerateUrls={saveAndGenerateUrls}
          redirectUrl={redirectUrl}
          useDefaultSheetNames={useDefaultSheetNames}
          previewSelectInputFields={previewSelectInputFields[activeTab]}
          previewLockInputFields={previewLockInputFields[activeTab]}
          fetchProductsWiseData={fetchProductsWiseData}
          subTypeId={subTypeId[activeTab]}
        />

        {/* content */}
        <TabContent
          fetchProductsWiseData={fetchProductsWiseData}
          filteredForMonth={month}
          filteredForYear={year}
          fetchRawAndFinalFileUrls={fetchRawAndFinalFileUrls}
          activeTab={activeTab}
          products={products}
          months={months}
          typeVal={sectionTypeValues[activeTab]}
          subTypeId={subTypeId?.[activeTab] || null}
        />
      </div>
    </div>
  );
}
